import { useNavigate } from "react-router-dom";
import { BookOpen, Star, Trophy, Zap, Sun, Heart, Church, Globe } from "lucide-react";
import { useLanguage } from "../hooks/useLanguage";
import LanguageToggle from "./LanguageToggle";

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: "#FAFAFA" }}>
      {/* Language Toggle - Top Right */}
      <div className="absolute top-4 right-4 z-50">
        <LanguageToggle variant="landing" />
      </div>

      {/* Hero - vivid rainbow gradient */}
      <div className="relative min-h-[62vh] flex items-center justify-center px-4 py-16 overflow-hidden"
        style={{ background: "linear-gradient(135deg, #EF476F 0%, #FFD166 30%, #06D6A0 60%, #118AB2 100%)" }}>
        {/* Decorative bubbles */}
        <div className="absolute top-6 left-6 w-24 h-24 rounded-full opacity-30" style={{ background: "#fff" }} />
        <div className="absolute bottom-8 right-8 w-36 h-36 rounded-full opacity-20" style={{ background: "#073B4C" }} />
        <div className="absolute top-1/3 right-12 w-10 h-10 rounded-full opacity-40" style={{ background: "#FFD166" }} />
        <div className="absolute bottom-1/4 left-16 w-8 h-8 rounded-full opacity-40" style={{ background: "#EF476F" }} />

        <div className="text-center max-w-xl mx-auto relative z-10">
          <div className="flex justify-center mb-5 fade-in-up fade-in-up-1">
            <div className="bg-white rounded-full p-5 shadow-xl kid-bounce">
              <BookOpen size={52} strokeWidth={2.5} style={{ color: "#118AB2" }} />
            </div>
          </div>
          <h1 className="text-6xl md:text-7xl font-black text-white mb-3 fade-in-up fade-in-up-2"
            style={{ fontFamily: "Fredoka, sans-serif", textShadow: "0 4px 16px rgba(0,0,0,0.3)" }}>
            Bible Buds
          </h1>
          <p className="text-xl md:text-2xl text-white font-bold mb-8 fade-in-up fade-in-up-3"
            style={{ fontFamily: "Quicksand, sans-serif", textShadow: "0 2px 8px rgba(0,0,0,0.25)" }}>
            {t('hero_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center fade-in-up fade-in-up-4">
            <button onClick={() => navigate("/auth")}
              className="bg-white font-black text-lg px-8 py-4 rounded-2xl shadow-xl hover:scale-105 active:scale-95 transition-all border-b-4 border-gray-200"
              style={{ fontFamily: "Fredoka, sans-serif", color: "#073B4C" }}
              data-testid="get-started-btn">
              {t('get_started')}
            </button>
            <button onClick={() => navigate("/auth")}
              className="bg-white/25 text-white font-bold text-lg px-8 py-4 rounded-2xl border-2 border-white hover:bg-white/40 transition-all"
              style={{ fontFamily: "Quicksand, sans-serif" }}
              data-testid="sign-in-btn">
              {t('parent_login') || 'Parent Login'}
            </button>
          </div>
          {/* Church Login Link */}
          <div className="mt-4 fade-in-up fade-in-up-4">
            <button onClick={() => navigate("/church/auth")}
              className="flex items-center gap-2 mx-auto text-white/80 font-semibold text-base hover:text-white transition-colors bg-white/10 px-5 py-2 rounded-xl hover:bg-white/20"
              style={{ fontFamily: "Quicksand, sans-serif" }}
              data-testid="church-login-btn">
              <Church size={18} />
              {t('church_login')}
            </button>
          </div>
          {/* Kids Login Link */}
          <div className="mt-6 fade-in-up fade-in-up-4">
            <button onClick={() => navigate("/kids")}
              className="px-8 py-3 rounded-full font-black text-lg text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
              style={{ fontFamily: "Fredoka, sans-serif", background: "linear-gradient(135deg, #9B5DE5, #7B2FF7)" }}
              data-testid="kids-login-btn">
              I'm a Kid!
            </button>
          </div>
        </div>
      </div>

      {/* What is Bible Buds? + See How It Works - Combined Section */}
      <div className="py-14 px-4" style={{ background: "linear-gradient(135deg, #9B5DE5 0%, #EF476F 100%)" }}>
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('what_is_bible_buds') || 'What is Bible Buds?'}
            </h2>
            <p className="text-white/90 text-lg max-w-2xl mx-auto font-medium leading-relaxed">
              {t('bible_buds_description') || 'Bible Buds is a daily Bible learning app designed for children ages 3-18. Each day, kids receive age-appropriate lessons with engaging stories, fun activities, drawing canvases, and quizzes. Parents can track progress and join family study sessions together!'}
            </p>
          </div>
          
          {/* Feature highlights */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <div className="bg-white/20 rounded-2xl p-4 text-center">
              <p className="text-3xl mb-1">📖</p>
              <p className="text-white font-bold text-sm">{t('daily_lessons_feature') || 'Daily Lessons'}</p>
            </div>
            <div className="bg-white/20 rounded-2xl p-4 text-center">
              <p className="text-3xl mb-1">🎨</p>
              <p className="text-white font-bold text-sm">{t('drawing_art_feature') || 'Drawing & Art'}</p>
            </div>
            <div className="bg-white/20 rounded-2xl p-4 text-center">
              <p className="text-3xl mb-1">👨‍👩‍👧‍👦</p>
              <p className="text-white font-bold text-sm">{t('family_study_feature') || 'Family Study'}</p>
            </div>
            <div className="bg-white/20 rounded-2xl p-4 text-center">
              <p className="text-3xl mb-1">🏆</p>
              <p className="text-white font-bold text-sm">{t('rewards_badges_feature') || 'Rewards & Badges'}</p>
            </div>
          </div>

          {/* Video */}
          <div className="text-center">
            <h3 className="text-xl font-black text-white mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('see_how_it_works') || 'See How It Works'}
            </h3>
            <div className="rounded-3xl overflow-hidden shadow-2xl mx-auto max-w-3xl border-4 border-white/20">
              <video 
                autoPlay
                muted
                loop
                playsInline
                controls 
                preload="auto"
                className="w-full"
                data-testid="promo-video"
              >
                <source src="/bible-buds-promo.mp4" type="video/mp4" />
                {t('video_not_supported') || 'Your browser does not support the video tag.'}
              </video>
            </div>
          </div>
        </div>
      </div>

      {/* Features - BOLD colored cards */}
      <div className="max-w-5xl mx-auto px-4 py-14">
        <h2 className="text-center text-3xl md:text-4xl font-black text-[#073B4C] mb-10"
          style={{ fontFamily: "Fredoka, sans-serif" }}>
          Everything kids need to grow in faith
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: BookOpen, color: "white", bg: "#118AB2", border: "#0d6e91", title: "Daily Lessons", desc: "Fresh Bible stories every day, just right for your child's age!", textColor: "white" },
            { icon: Star, color: "#073B4C", bg: "#FFD166", border: "#e0b040", title: "Fun Activities", desc: "Questions, journals & challenges to grow in faith!", textColor: "#073B4C" },
            { icon: Trophy, color: "white", bg: "#06D6A0", border: "#049a73", title: "Earn Rewards", desc: "Collect badges and build streaks as you learn!", textColor: "white" },
          ].map((f, i) => (
            <div key={i} className="rounded-3xl p-7 shadow-lg hover:scale-105 transition-all duration-300 border-b-4"
              style={{ backgroundColor: f.bg, borderColor: f.border }}>
              <div className="bg-white/20 rounded-2xl w-14 h-14 flex items-center justify-center mb-4">
                <f.icon size={30} color={f.color} strokeWidth={2.5} />
              </div>
              <h3 className="text-xl font-black mb-2" style={{ fontFamily: "Fredoka, sans-serif", color: f.textColor }}>{f.title}</h3>
              <p className="text-sm font-semibold leading-relaxed" style={{ color: f.textColor, opacity: 0.85 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Age groups - colorful split */}
      <div className="py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-center text-3xl font-black text-[#073B4C] mb-8" style={{ fontFamily: "Fredoka, sans-serif" }}>
            Made for every age
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="rounded-3xl p-7 shadow-lg border-b-4" style={{ background: "linear-gradient(135deg, #FFD166, #F77F00)", borderColor: "#e0b040" }}>
              <Sun size={42} color="white" strokeWidth={2.5} className="mb-3" />
              <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Little Lights (3–7)
              </h3>
              <p className="text-white/90 font-semibold">Simple words, big faith. Fun illustrations and easy activities just for little ones.</p>
            </div>
            <div className="rounded-3xl p-7 shadow-lg border-b-4" style={{ background: "linear-gradient(135deg, #118AB2, #073B4C)", borderColor: "#0a6282" }}>
              <Zap size={42} color="#FFD166" strokeWidth={2.5} className="mb-3" />
              <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Bible Explorers (8–12)
              </h3>
              <p className="text-white/90 font-semibold">Deeper lessons, journal prompts, and creative challenges to go further in faith.</p>
            </div>
            <div className="rounded-3xl p-7 shadow-lg border-b-4" style={{ background: "linear-gradient(135deg, #9B5DE5, #6B21A8)", borderColor: "#5b189c" }}>
              <Trophy size={42} color="#FFD166" strokeWidth={2.5} className="mb-3" />
              <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Faith Builders (13–18)
              </h3>
              <p className="text-white/90 font-semibold">Mature topics, thoughtful reflection, and real-world faith application for teens.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Family Study promo - vibrant orange */}
      <div className="py-14 px-4">
        <div className="max-w-4xl mx-auto rounded-3xl p-8 shadow-xl text-center border-b-4"
          style={{ background: "linear-gradient(135deg, #EF476F, #9B5DE5)", borderColor: "#c8375a" }}>
          <Heart size={44} color="white" className="mx-auto mb-4" strokeWidth={2.5} />
          <h2 className="text-3xl font-black text-white mb-3" style={{ fontFamily: "Fredoka, sans-serif" }}>
            Family Bible Study Time
          </h2>
          <p className="text-white/90 font-semibold text-lg mb-6">
            After the kids finish their lessons, gather the whole family! Get discussion questions, prayer prompts, and notes to study together.
          </p>
          <button onClick={() => navigate("/auth")}
            className="bg-white font-black text-lg px-8 py-4 rounded-2xl shadow-xl hover:scale-105 transition-all"
            style={{ fontFamily: "Fredoka, sans-serif", color: "#EF476F" }}>
            Start Your Family Journey
          </button>
        </div>
      </div>

      {/* Church section */}
      <div className="py-14 px-4" style={{ background: "linear-gradient(135deg, #073B4C 0%, #118AB2 100%)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-black text-white mb-3" style={{ fontFamily: "Fredoka, sans-serif" }}>
            For Churches
          </h2>
          <p className="text-white/80 font-semibold text-lg mb-6 max-w-2xl mx-auto">
            Manage your entire congregation's Bible learning. Track family progress, create church-wide studies, send announcements, and view leaderboards.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <button onClick={() => navigate("/church/auth")}
              className="bg-white font-black text-lg px-8 py-4 rounded-2xl shadow-xl hover:scale-105 transition-all"
              style={{ fontFamily: "Fredoka, sans-serif", color: "#118AB2" }}
              data-testid="church-signup-btn">
              Register Your Church
            </button>
            <div className="text-white/60 text-sm self-center">$39.99/month · Up to 50 families</div>
          </div>
          
          {/* Church Family Sign In */}
          <div className="border-t border-white/20 pt-6 mt-4">
            <p className="text-white/70 text-sm mb-3">Already part of a church using Bible Buds?</p>
            <button onClick={() => navigate("/church-family/auth")}
              className="bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3 rounded-xl transition-all"
              data-testid="church-family-signin-btn">
              Sign In as Church Family
            </button>
          </div>
        </div>
      </div>

      {/* Watch More - Second Video Section */}
      <div className="py-14 px-4" style={{ background: "linear-gradient(135deg, #118AB2 0%, #073B4C 100%)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>
            Watch Bible Buds in Action
          </h2>
          <p className="text-white/80 font-semibold mb-8">
            See how families are growing in faith together
          </p>
          
          {/* Video */}
          <div className="rounded-3xl overflow-hidden shadow-2xl mx-auto max-w-2xl mb-6">
            <video 
              autoPlay={true}
              muted={true}
              loop={true}
              playsInline={true}
              controls 
              preload="auto"
              className="w-full"
              data-testid="features-video"
            >
              <source src="https://customer-assets.emergentagent.com/job_bbad70b4-2766-4064-ad5a-b1e988554756/artifacts/lmlkwksv_bible-buds-promo%20%281%29.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          
          {/* Download link */}
          <a 
            href="/bible-buds-promo.mp4" 
            download="BibleBuds-Promo.mp4"
            className="inline-flex items-center gap-2 bg-white text-[#118AB2] font-bold px-6 py-3 rounded-xl hover:bg-white/90 transition-all"
            data-testid="download-video-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download Promo Video
          </a>
        </div>
      </div>

      {/* CTA footer */}
      <div className="py-14 px-4 text-center" style={{ background: "#073B4C" }}>
        <h2 className="text-3xl md:text-4xl font-black text-white mb-3" style={{ fontFamily: "Fredoka, sans-serif" }}>
          Start your child's Bible adventure today!
        </h2>
        <p className="text-white/80 mb-2 text-lg font-semibold">7-day free trial, then $9.99/month</p>
        <p className="text-white/50 mb-8 text-sm">Cancel anytime. Annual plan available for $69.99/year.</p>
        <button onClick={() => navigate("/auth")}
          className="font-black text-xl px-10 py-4 rounded-2xl border-b-4 active:border-b-0 active:translate-y-1 transition-all pulse-ring"
          style={{ fontFamily: "Fredoka, sans-serif", background: "#06D6A0", borderColor: "#049a73", color: "white" }}
          data-testid="cta-start-btn">
          Start Free Trial
        </button>
        
        {/* Footer Links */}
        <div className="mt-10 pt-6 border-t border-white/20">
          <div className="flex justify-center gap-6 mb-4">
            <button onClick={() => navigate("/terms")} className="text-white/60 hover:text-white text-sm font-medium">
              Terms of Service
            </button>
            <button onClick={() => navigate("/privacy")} className="text-white/60 hover:text-white text-sm font-medium">
              Privacy Policy
            </button>
          </div>
          <p className="text-white/60 text-sm">
            Need help? Contact us at{" "}
            <a href="mailto:support@biblebuds.space" className="text-[#06D6A0] hover:text-[#05b588] font-semibold">
              support@biblebuds.space
            </a>
          </p>
          <p className="text-white/40 text-xs mt-4">© 2026 Bible Buds. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
